import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations object
const translations = {
  ar: {
    // Navigation
    home: 'الرئيسية',
    about: 'نبذة عني',
    projects: 'مشاريعي',
    blog: 'المدونة',
    contact: 'تواصل معي',
    
    // Common
    loading: 'جاري التحميل...',
    error: 'حدث خطأ',
    success: 'نجح',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    update: 'تحديث',
    search: 'بحث',
    filter: 'تصفية',
    sort: 'ترتيب',
    view: 'عرض',
    download: 'تحميل',
    upload: 'رفع',
    close: 'إغلاق',
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    yes: 'نعم',
    no: 'لا',
    
    // Auth
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    rememberMe: 'تذكرني',
    forgotPassword: 'نسيت كلمة المرور',
    adminLogin: 'دخول الأدمن',
    
    // Portfolio
    portfolio: 'معرض أعمالي',
    personalInfo: 'المعلومات الشخصية',
    skills: 'المهارات',
    experience: 'الخبرات',
    education: 'التعليم',
    contactInfo: 'معلومات التواصل',
    downloadCV: 'تحميل السيرة الذاتية',
    
    // Projects
    myProjects: 'مشاريعي',
    allProjects: 'جميع المشاريع',
    projectDetails: 'تفاصيل المشروع',
    projectDescription: 'وصف المشروع',
    technologies: 'التقنيات المستخدمة',
    projectDemo: 'عرض المشروع',
    projectSource: 'كود المصدر',
    screenshots: 'لقطات الشاشة',
    
    // Blog
    blog: 'المدونة',
    articles: 'المقالات',
    readMore: 'اقرأ المزيد',
    publishedOn: 'نُشر في',
    author: 'المؤلف',
    categories: 'التصنيفات',
    tags: 'العلامات',
    
    // Contact
    contactMe: 'تواصل معي',
    sendMessage: 'إرسال رسالة',
    yourName: 'اسمك',
    yourEmail: 'بريدك الإلكتروني',
    yourMessage: 'رسالتك',
    messageSent: 'تم إرسال الرسالة',
    messageFailed: 'فشل في إرسال الرسالة',
    
    // Admin Dashboard
    dashboard: 'لوحة التحكم',
    admin: 'الإدارة',
    portfolio: 'معرض الأعمال',
    management: 'الإدارة',
    settings: 'الإعدادات',
    chat: 'الرسائل',
    statistics: 'الإحصائيات',
    
    // Forms
    required: 'مطلوب',
    optional: 'اختياري',
    title: 'العنوان',
    description: 'الوصف',
    content: 'المحتوى',
    status: 'الحالة',
    published: 'منشور',
    draft: 'مسودة',
    active: 'نشط',
    inactive: 'غير نشط',
    
    // Messages
    noData: 'لا توجد بيانات',
    noResults: 'لا توجد نتائج',
    confirmDelete: 'هل أنت متأكد من الحذف؟',
    savedSuccessfully: 'تم الحفظ بنجاح',
    deletedSuccessfully: 'تم الحذف بنجاح',
    updatedSuccessfully: 'تم التحديث بنجاح',
    
    // Time
    justNow: 'الآن',
    minuteAgo: 'منذ دقيقة',
    minutesAgo: 'منذ دقائق',
    hourAgo: 'منذ ساعة',
    hoursAgo: 'منذ ساعات',
    dayAgo: 'منذ يوم',
    daysAgo: 'منذ أيام',
    weekAgo: 'منذ أسبوع',
    weeksAgo: 'منذ أسابيع',
    monthAgo: 'منذ شهر',
    monthsAgo: 'منذ أشهر',
    yearAgo: 'منذ سنة',
    yearsAgo: 'منذ سنوات'
  },
  
  en: {
    // Navigation
    home: 'Home',
    about: 'About',
    projects: 'Projects',
    blog: 'Blog',
    contact: 'Contact',
    
    // Common
    loading: 'Loading...',
    error: 'Error occurred',
    success: 'Success',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    view: 'View',
    download: 'Download',
    upload: 'Upload',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    yes: 'Yes',
    no: 'No',
    
    // Auth
    login: 'Login',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password',
    adminLogin: 'Admin Login',
    
    // Portfolio
    portfolio: 'Portfolio',
    personalInfo: 'Personal Information',
    skills: 'Skills',
    experience: 'Experience',
    education: 'Education',
    contactInfo: 'Contact Information',
    downloadCV: 'Download CV',
    
    // Projects
    myProjects: 'My Projects',
    allProjects: 'All Projects',
    projectDetails: 'Project Details',
    projectDescription: 'Project Description',
    technologies: 'Technologies Used',
    projectDemo: 'Live Demo',
    projectSource: 'Source Code',
    screenshots: 'Screenshots',
    
    // Blog
    blog: 'Blog',
    articles: 'Articles',
    readMore: 'Read More',
    publishedOn: 'Published on',
    author: 'Author',
    categories: 'Categories',
    tags: 'Tags',
    
    // Contact
    contactMe: 'Contact Me',
    sendMessage: 'Send Message',
    yourName: 'Your Name',
    yourEmail: 'Your Email',
    yourMessage: 'Your Message',
    messageSent: 'Message sent successfully',
    messageFailed: 'Failed to send message',
    
    // Admin Dashboard
    dashboard: 'Dashboard',
    admin: 'Admin',
    portfolio: 'Portfolio',
    management: 'Management',
    settings: 'Settings',
    chat: 'Messages',
    statistics: 'Statistics',
    
    // Forms
    required: 'Required',
    optional: 'Optional',
    title: 'Title',
    description: 'Description',
    content: 'Content',
    status: 'Status',
    published: 'Published',
    draft: 'Draft',
    active: 'Active',
    inactive: 'Inactive',
    
    // Messages
    noData: 'No data available',
    noResults: 'No results found',
    confirmDelete: 'Are you sure you want to delete?',
    savedSuccessfully: 'Saved successfully',
    deletedSuccessfully: 'Deleted successfully',
    updatedSuccessfully: 'Updated successfully',
    
    // Time
    justNow: 'Just now',
    minuteAgo: '1 minute ago',
    minutesAgo: 'minutes ago',
    hourAgo: '1 hour ago',
    hoursAgo: 'hours ago',
    dayAgo: '1 day ago',
    daysAgo: 'days ago',
    weekAgo: '1 week ago',
    weeksAgo: 'weeks ago',
    monthAgo: '1 month ago',
    monthsAgo: 'months ago',
    yearAgo: '1 year ago',
    yearsAgo: 'years ago'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ar');
  const [direction, setDirection] = useState('rtl');

  // Initialize language from localStorage or browser
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const browserLanguage = navigator.language.split('-')[0];
    
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else if (['ar', 'en'].includes(browserLanguage)) {
      setLanguage(browserLanguage);
    }
  }, []);

  // Update direction when language changes
  useEffect(() => {
    const newDirection = language === 'ar' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    document.documentElement.lang = language;
    document.documentElement.dir = newDirection;
    localStorage.setItem('language', language);
  }, [language]);

  // Toggle language function
  const toggleLanguage = () => {
    const newLanguage = language === 'ar' ? 'en' : 'ar';
    setLanguage(newLanguage);
  };

  // Set specific language
  const setLanguageCode = (lang) => {
    if (['ar', 'en'].includes(lang)) {
      setLanguage(lang);
    }
  };

  // Translation function
  const t = (key) => {
    return translations[language][key] || key;
  };

  // Check if text is Arabic
  const isArabic = (text) => {
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text);
  };

  // Format date based on language
  const formatDate = (date, options = {}) => {
    const locale = language === 'ar' ? 'ar-EG' : 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    });
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    
    const intervals = [
      { label: 'year', seconds: 31536000 },
      { label: 'month', seconds: 2592000 },
      { label: 'week', seconds: 604800 },
      { label: 'day', seconds: 86400 },
      { label: 'hour', seconds: 3600 },
      { label: 'minute', seconds: 60 }
    ];
    
    for (const interval of intervals) {
      const count = Math.floor(diffInSeconds / interval.seconds);
      if (count >= 1) {
        return t(`${interval.label}Ago`).replace('1 ', count + ' ');
      }
    }
    
    return t('justNow');
  };

  const value = {
    language,
    direction,
    toggleLanguage,
    setLanguage: setLanguageCode,
    t,
    isArabic,
    formatDate,
    formatRelativeTime,
    translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};